import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useDashReport } from '../hooks/useDashReport'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface EspecialidadItem {
  nombre: string
  total: number
}

interface PaisItem {
  nombre: string
  total: number
  especialidades: EspecialidadItem[]
}

interface Allegra0226Data {
  titulo: string
  subtitulo: string
  total_general: number
  total_paises: number
  total_especialidades: number
  paises: PaisItem[]
  especialidades_unicas: string[]
  datos_raw: Record<string, Record<string, number>>
}

// Colores para especialidades (degradado azul basado en valor)
function getEspColors(valores: number[]) {
  const maxVal = Math.max(...valores)
  const minVal = Math.min(...valores)
  const rango = maxVal - minVal || 1
  const bgColors = valores.map(v => {
    const t = (v - minVal) / rango
    const alpha = 0.3 + t * 0.7
    return `rgba(37, 99, 235, ${alpha})`
  })
  const borderColors = valores.map(v => {
    const t = (v - minVal) / rango
    const r = Math.round(37 - t * 7)
    const g = Math.round(99 - t * 30)
    const b = Math.round(235 - t * 30)
    return `rgb(${r}, ${g}, ${b})`
  })
  return { bgColors, borderColors }
}

// Colores por país
const coloresPais: Record<string, { bg: string; border: string; badgeBg: string; badgeText: string }> = {
  'Ecuador': { bg: 'rgba(30, 64, 175, 0.7)', border: '#1e40af', badgeBg: 'bg-blue-200', badgeText: 'text-blue-900' },
  'Colombia': { bg: 'rgba(234, 179, 8, 0.7)', border: '#eab308', badgeBg: 'bg-yellow-100', badgeText: 'text-yellow-800' },
  'Panamá': { bg: 'rgba(16, 185, 129, 0.7)', border: '#10b981', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-800' },
  'Costa Rica': { bg: 'rgba(239, 68, 68, 0.7)', border: '#ef4444', badgeBg: 'bg-red-100', badgeText: 'text-red-800' },
  'Guatemala': { bg: 'rgba(56, 189, 248, 0.7)', border: '#38bdf8', badgeBg: 'bg-sky-100', badgeText: 'text-sky-800' },
}

const coloresPaisFallback = [
  { bg: 'rgba(139, 92, 246, 0.7)', border: '#8b5cf6', badgeBg: 'bg-violet-100', badgeText: 'text-violet-800' },
  { bg: 'rgba(236, 72, 153, 0.7)', border: '#ec4899', badgeBg: 'bg-pink-100', badgeText: 'text-pink-800' },
]

function getColorPais(nombre: string, allPaises: string[]) {
  if (coloresPais[nombre]) return coloresPais[nombre]
  const unknowns = allPaises.filter(k => !coloresPais[k])
  const idx = unknowns.indexOf(nombre)
  return coloresPaisFallback[idx >= 0 ? idx % coloresPaisFallback.length : 0]
}

export function Allegra0226Page() {
  const { data, isLoading, error } = useDashReport<Allegra0226Data>('opella/allegra-0226')
  const [filtroPais, setFiltroPais] = useState('todos')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('todas')
  const [detalleExpanded, setDetalleExpanded] = useState(false)
  const detalleRef = useRef<HTMLDivElement>(null)

  // Build flat records from datos_raw
  const registros = useMemo(() => {
    if (!data?.datos_raw) return []
    const result: { pais: string; especialidad: string; total: number }[] = []
    for (const [paisNombre, especialidades] of Object.entries(data.datos_raw)) {
      for (const [espNombre, total] of Object.entries(especialidades)) {
        result.push({ pais: paisNombre, especialidad: espNombre, total })
      }
    }
    return result
  }, [data])

  const allPaisNames = useMemo(() => {
    if (!data?.datos_raw) return []
    return Object.keys(data.datos_raw)
  }, [data])

  // Filtered records
  const registrosFiltrados = useMemo(() => {
    return registros.filter(r => {
      const matchPais = filtroPais === 'todos' || r.pais === filtroPais
      const matchEsp = filtroEspecialidad === 'todas' || r.especialidad === filtroEspecialidad
      return matchPais && matchEsp
    })
  }, [registros, filtroPais, filtroEspecialidad])

  const totalFiltrado = useMemo(() => registrosFiltrados.reduce((s, r) => s + r.total, 0), [registrosFiltrados])

  // Available especialidades based on pais filter
  const especialidadesDisponibles = useMemo(() => {
    const filtered = filtroPais === 'todos'
      ? registros
      : registros.filter(r => r.pais === filtroPais)
    const espMap: Record<string, number> = {}
    filtered.forEach(r => {
      espMap[r.especialidad] = (espMap[r.especialidad] || 0) + r.total
    })
    return Object.entries(espMap)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({ nombre, total }))
  }, [registros, filtroPais])

  // Stats
  const paisesVisibles = useMemo(() => [...new Set(registrosFiltrados.map(r => r.pais))], [registrosFiltrados])
  const espsVisibles = useMemo(() => [...new Set(registrosFiltrados.map(r => r.especialidad))], [registrosFiltrados])

  // Dynamic title
  const tituloResumen = useMemo(() => {
    if (filtroPais === 'todos' && filtroEspecialidad === 'todas') return 'Resumen General'
    if (filtroPais !== 'todos' && filtroEspecialidad === 'todas') return filtroPais
    return filtroEspecialidad
  }, [filtroPais, filtroEspecialidad])

  const labelTotal = useMemo(() => {
    if (filtroPais === 'todos' && filtroEspecialidad === 'todas') return 'Total Médicos'
    if (filtroPais !== 'todos' && filtroEspecialidad === 'todas') return `Total ${filtroPais}`
    return `Total ${filtroEspecialidad}`
  }, [filtroPais, filtroEspecialidad])

  // Chart data: especialidad (horizontal bars)
  const espChartData = useMemo(() => {
    const espMap: Record<string, number> = {}
    registrosFiltrados.forEach(r => {
      espMap[r.especialidad] = (espMap[r.especialidad] || 0) + r.total
    })
    const sorted = Object.entries(espMap).sort((a, b) => b[1] - a[1])
    const labels = sorted.map(e => e[0])
    const valores = sorted.map(e => e[1])
    const { bgColors, borderColors } = getEspColors(valores)
    return { labels, valores, bgColors, borderColors }
  }, [registrosFiltrados])

  // Chart data: pais (vertical bars)
  const paisChartData = useMemo(() => {
    const paisMap: Record<string, number> = {}
    registrosFiltrados.forEach(r => {
      paisMap[r.pais] = (paisMap[r.pais] || 0) + r.total
    })
    const sorted = Object.entries(paisMap).sort((a, b) => b[1] - a[1])
    return {
      labels: sorted.map(p => p[0]),
      valores: sorted.map(p => p[1]),
      bgColors: sorted.map(p => getColorPais(p[0], allPaisNames).bg),
      borderColors: sorted.map(p => getColorPais(p[0], allPaisNames).border),
    }
  }, [registrosFiltrados, allPaisNames])

  const handleFiltroPais = useCallback((nombre: string) => {
    setFiltroPais(nombre)
    setFiltroEspecialidad('todas')
  }, [])

  const handleFiltroEspecialidad = useCallback((nombre: string) => {
    setFiltroEspecialidad(nombre)
  }, [])

  const toggleDetalle = useCallback(() => {
    setDetalleExpanded(prev => !prev)
  }, [])

  // Auto-resize collapsible
  useEffect(() => {
    if (detalleRef.current) {
      if (detalleExpanded) {
        detalleRef.current.style.maxHeight = detalleRef.current.scrollHeight + 'px'
        detalleRef.current.style.opacity = '1'
      } else {
        detalleRef.current.style.maxHeight = '0px'
        detalleRef.current.style.opacity = '0'
      }
    }
  }, [detalleExpanded, registrosFiltrados])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Filtro por País */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-semibold text-gray-900">Filtrar por País:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleFiltroPais('todos')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  filtroPais === 'todos'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              {data.paises.map(pais => (
                <button
                  key={pais.nombre}
                  onClick={() => handleFiltroPais(pais.nombre)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroPais === pais.nombre
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pais.nombre} <span className="text-xs opacity-75">({pais.total})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filtro por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="font-semibold text-gray-900">Filtrar por Especialidad:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleFiltroEspecialidad('todas')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  filtroEspecialidad === 'todas'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              {especialidadesDisponibles.map(esp => (
                <button
                  key={esp.nombre}
                  onClick={() => handleFiltroEspecialidad(esp.nombre)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroEspecialidad === esp.nombre
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {esp.nombre} <span className="text-xs opacity-75">({esp.total.toLocaleString('es-EC')})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>{tituloResumen}</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl lg:text-4xl font-bold text-gray-900">{totalFiltrado.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-2xl lg:text-3xl font-bold text-purple-600">{paisesVisibles.length}</p>
              <p className="text-sm text-gray-600 mt-1">Países</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-2xl lg:text-3xl font-bold text-blue-600">{espsVisibles.length}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades</p>
            </div>
          </div>
        </div>

        {/* Gráficas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Gráfica por Especialidad (barras horizontales) */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Distribución por Especialidad
            </h3>
            <div className="h-64 lg:h-96">
              <Bar
                data={{
                  labels: espChartData.labels,
                  datasets: [{
                    label: 'Total Médicos',
                    data: espChartData.valores,
                    backgroundColor: espChartData.bgColors,
                    borderColor: espChartData.borderColors,
                    borderWidth: 1,
                    borderRadius: { topRight: 6, bottomRight: 6 },
                    borderSkipped: false,
                  }],
                }}
                plugins={[ChartDataLabels]}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: { padding: { right: 80 } },
                  plugins: {
                    legend: { display: false },
                    datalabels: {
                      anchor: 'end',
                      align: 'right',
                      offset: 4,
                      clip: false,
                      color: '#6b7280',
                      font: { size: 11, weight: 500 },
                      formatter: (v: number) => v.toLocaleString('es-EC'),
                    },
                  },
                  scales: {
                    x: { display: false },
                  },
                }}
              />
            </div>
          </div>

          {/* Gráfica por País (barras verticales) */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Distribución por País
            </h3>
            <div className="h-64 lg:h-96">
              <Bar
                data={{
                  labels: paisChartData.labels,
                  datasets: [{
                    label: 'Total Médicos',
                    data: paisChartData.valores,
                    backgroundColor: paisChartData.bgColors,
                    borderColor: paisChartData.borderColors,
                    borderWidth: 1,
                  }],
                }}
                plugins={[ChartDataLabels]}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: { padding: { top: 30 } },
                  plugins: {
                    legend: { display: false },
                    datalabels: {
                      anchor: 'end',
                      align: 'top',
                      offset: 2,
                      clip: false,
                      color: '#6b7280',
                      font: { size: 12, weight: 500 },
                      formatter: (value: number) => {
                        const pct = totalFiltrado > 0 ? ((value / totalFiltrado) * 100).toFixed(1) : '0'
                        return value.toLocaleString('es-EC') + ' (' + pct + '%)'
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grace: '10%',
                      ticks: {
                        callback: (v) => Number(v).toLocaleString('es-EC'),
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Tabla de Detalle */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
          <button
            onClick={toggleDetalle}
            className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="text-base lg:text-lg">Detalle por País y Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2">({registrosFiltrados.length} registros)</span>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${detalleExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <div
            ref={detalleRef}
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ maxHeight: 0, opacity: 0 }}
          >
            <div className="overflow-x-auto mt-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">País</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especialidad</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Médicos</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% del Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {registrosFiltrados.map((r, idx) => {
                    const color = getColorPais(r.pais, allPaisNames)
                    const pct = totalFiltrado > 0 ? ((r.total / totalFiltrado) * 100).toFixed(1) : '0'
                    return (
                      <tr key={`${r.pais}-${r.especialidad}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.badgeBg} ${color.badgeText}`}>
                            {r.pais}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.especialidad}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">{r.total}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{pct}%</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-900">Total</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-blue-700">{totalFiltrado.toLocaleString('es-EC')}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-700">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-12 py-6 text-center">
        <div className="text-xs text-gray-400">
          <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
            Tonal Enterprise
          </a>
          <span className="mx-2">&bull;</span>
          <span>2026 Tonal Strategies</span>
        </div>
      </footer>
    </div>
  )
}
